// Formulário de Transferência de Stablecoins
// - Lista de stablecoins disponíveis na carteira
// - Conversão BRL/USD em tempo real com timer visual
// - Envio de ativos via MetaMask

import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, RefreshCw, Clock, AlertCircle, CheckCircle, Loader2, Percent, UserPlus, Bookmark, X, QrCode, Copy, Maximize2, ScanLine, Shrink, EyeOff, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Modal from '@/components/ui/Modal';
import QRScanner from '@/components/transfer/QRScanner';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { useWallets } from '@/contexts/WalletContext';
import { useToast } from '@/hooks/useToast';
import { IncomingTransferContext } from '@/contexts/IncomingTransferContext';
import { useDevLog, isLocalNetwork } from '@/contexts/DevLogContext';
import { usePendingFees } from '@/contexts/PendingFeeContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/contexts/AuthContext';
import { getFeeAddress, IS_FEE_ENABLED } from '@/config/feeConfig';
import { STABLECOIN_CONTRACTS } from '@/config/stablecoinContracts';
import { formatAddress, getAddressOrWalletName } from '@/utils/formatters';
import { formatCurrencyDisplay, formatAmountForInput, formatAmountForBlur, parseAmountFromInput, sanitizeAmountInput } from '@/utils/currency';
import { logQRParamsResult } from '@/services/loggingService';
import { createPaymentRequest, getPaymentRequest } from '@/services/paymentRequestService';
import { getBlockExplorerTxUrl } from '@/utils/blockExplorer';
import { hasShownAddToListPrompt, markAddToListPromptShown } from '@/utils/addToListPromptSession';
import { normalizeChainId, getEffectiveWalletChainId, isNetworkMismatch } from '@/utils/networkSync';
import { getTransferFormValidationError } from '@/utils/transferRules';
import { sendStablecoinTransferCompletao } from '@/utils/sendStablecoinTransferCompletao';
import { addPendingTxReconcile, removePendingTxReconcile } from '@/utils/pendingTxReconcile';
import { getReadOnlyProvider } from '@/utils/publicRpcReadOnly';
import { isMobile as isMobilePlatform } from '@/utils/platform';
import { getCurrentUser, getStoredGooglePhone } from '@/services/firebase';
import { createTransferNotification } from '@/services/transferNotificationService';
import { useAdmin } from '@/contexts/AdminContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';

// Feature flag: convite "Adicionar à lista de carteiras" a partir de movimentações.
// Mantido para possível reativação futura; por enquanto o link fica oculto.
const SHOW_ADD_TO_WALLET_FROM_MOVEMENTS = false;

// Ícones de stablecoins e bandeiras (moeda de exibição)
import usdcIcon from '@/assets/images/usdc.svg';
import usdtIcon from '@/assets/images/usdt.svg';
import brazilFlag from '@/assets/images/brazil.svg';
import usFlag from '@/assets/images/united_states.svg';

// Mapeamento de símbolo para ícone
const TOKEN_ICONS = {
  'USDC': usdcIcon,
  'USDT': usdtIcon,
};

// Parâmetros das redes para adicionar no MetaMask se necessário
const NETWORK_PARAMS = {
  '0x2105': {
    chainId: '0x2105',
    chainName: 'Base',
    nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
  },
  '0x14a34': {
    chainId: '0x14a34',
    chainName: 'Base Sepolia',
    nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://sepolia.base.org'],
    blockExplorerUrls: ['https://sepolia.basescan.org'],
  },
  '0xa4b1': {
    chainId: '0xa4b1',
    chainName: 'Arbitrum One',
    nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://arb1.arbitrum.io/rpc'],
    blockExplorerUrls: ['https://arbiscan.io'],
  },
  '0x89': {
    chainId: '0x89',
    chainName: 'Polygon',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrls: ['https://polygon-rpc.com'],
    blockExplorerUrls: ['https://polygonscan.com'],
  },
  '0xaa36a7': {
    chainId: '0xaa36a7',
    chainName: 'Sepolia',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://rpc.sepolia.org', 'https://ethereum-sepolia-rpc.publicnode.com'],
    blockExplorerUrls: ['https://sepolia.etherscan.io'],
  },
};

export default function TransferForm({ onOpenCarteiras, onOpenPendingFeeModal }) {
  const { t } = useTranslation();
  const {
    connectedWallet,
    walletAddress,
    walletChain,
    walletStablecoinBalances,
    selectedNetwork,
    supportedNetworks,
    switchNetwork,
    refreshWalletBalances,
    getChainDisplayName,
    provider: metamaskProvider,
    isWalletInitializing,
    isPhantomEVM,
  } = useWalletConnection();
  
  const {
    rates,
    convert,
    calculateFee,
    calculateBillingFee,
    secondsUntilUpdate,
    timerProgress,
    isLoading: ratesLoading,
    feePercent,
    feeBillingPercent,
    feeEnabled,
    minSendChargeAmount,
  } = useCurrencyConversion();
  
  const { success, error: showError, info } = useToast();
  const { wallets } = useWallets();
  const { addNotification } = useNotifications();
  const { incomingTransfer } = useContext(IncomingTransferContext) || {};
  const walletPickerRef = useRef(null);
  const {
    absorbTransferFee,
    convertAmountOnCurrencyChange,
    displayCurrency: settingsDisplayCurrency,
    lockDisplayCurrency,
    updateSetting: updateSettings,
    enableFirestoreNotifications,
    enableCreateNotificationOnSend,
    includeNameInWalletQR,
    includeEmailInWalletQR,
    includePhoneInWalletQR,
    includeNetworkInWalletQR,
    includePhotoInWalletQR,
  } = useSettings();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    recipient: '',
    currency: settingsDisplayCurrency === 'BRL' ? 'BRL' : 'USD',
    amount: '',
    selectedToken: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [txHash, setTxHash] = useState(null);
  const [feesTxHash, setFeesTxHash] = useState(null);
  const [transferConfirmed, setTransferConfirmed] = useState(false);   // confirmação na blockchain
  const [lastConfirmedDetail, setLastConfirmedDetail] = useState(null); // { at, amount, recipient, token } para mensagem
  const [feeConfirmed, setFeeConfirmed] = useState(false);
  const [transferStep, setTransferStep] = useState(null); // 'recipient' | 'fee' | null
  const [showWalletPicker, setShowWalletPicker] = useState(false);
  const [isRecipientFromList, setIsRecipientFromList] = useState(false); // Rastreia se endereço veio da lista
  const [transferMode, setTransferMode] = useState('enviar'); // 'enviar' | 'cobranca'
  const [showQRModal, setShowQRModal] = useState(false);
  const [paymentRequestURL, setPaymentRequestURL] = useState('');
  const [paymentRequestPayload, setPaymentRequestPayload] = useState(''); // Dados no QR (JSON) — evita abrir URL no mobile
  const [showQRInline, setShowQRInline] = useState(false); // Versão alternativa: QR na tela
  const [showQRScanner, setShowQRScanner] = useState(false); // Scanner de QR (modo Enviar)
  const [showWalletQRScanner, setShowWalletQRScanner] = useState(false); // Scanner de QR de carteira (Configurações)
  const [showSendHelp, setShowSendHelp] = useState(false);
  const [showChargeHelp, setShowChargeHelp] = useState(false);
  const [transferCooldownUntil, setTransferCooldownUntil] = useState(0); // após falha/rejeição, não reenviar por alguns segundos
  const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(0); // para exibir "Aguarde Xs" no botão
  const pendingTransferRef = useRef(null); // dados da tx em andamento (para recuperação ao voltar à aba)
  const transferSuccessAppliedRef = useRef(false);
  const sendAttemptRef = useRef(0);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const currencyDropdownRef = useRef(null);
  const [chargeAddressDisplayMode, setChargeAddressDisplayMode] = useLocalStorage('pcp_charge_address_display', 'full'); // 'full' | 'short' | 'hidden'
  const [chargeAddressCopyFeedback, setChargeAddressCopyFeedback] = useState(false);
  const chargeAddressCopyFeedbackTimeoutRef = useRef(null);
  /** Payload de carteira (nome, email, telefone, rede, photoURL) quando o destinatário veio de QR de carteira — usado ao abrir "Adicionar à lista". */
  const [recipientWalletPayload, setRecipientWalletPayload] = useState(null);
  /** Perfil do cobrador (receiver) vindo do payment request — quem paga pode adicionar a carteira com nome, email, etc. */
  const [receiverProfileFromPaymentRequest, setReceiverProfileFromPaymentRequest] = useState(null);
  useEffect(() => {
    if (chargeAddressCopyFeedbackTimeoutRef.current) clearTimeout(chargeAddressCopyFeedbackTimeoutRef.current);
  }, []);

  // Destinatário fora da lista: mostramos link "Adicionar à lista"; marcamos como mostrado só no clique (evita sumir com Strict Mode)
  const recipientNotInList = lastConfirmedDetail?.recipient && !wallets.some((w) => w?.address && String(w.address).toLowerCase() === String(lastConfirmedDetail.recipient).toLowerCase());

  // Limpar payload quando o usuário alterar o destinatário (não é mais o do QR)
  useEffect(() => {
    if (recipientWalletPayload?.address && formData.recipient?.trim() && String(formData.recipient).toLowerCase() !== String(recipientWalletPayload.address).toLowerCase()) {
      setRecipientWalletPayload(null);
    }
  }, [formData.recipient, recipientWalletPayload?.address]);

  const activeWalletRecord = useMemo(() => {
    if (!walletAddress || !wallets?.length) return null;
    const addr = String(walletAddress).toLowerCase();
    return wallets.find((w) => w?.address && String(w.address).toLowerCase() === addr) || null;
  }, [walletAddress, wallets]);

  const { log: devLog } = useDevLog();
  const mmLog = useCallback((msg, data) => {
    devLog(`[MM_LOOP] ${new Date().toISOString()} ${msg}`, data);
  }, [devLog]);

  const txLog = useCallback(
    (stage, extra) => {
      const now = new Date().toISOString();
      const baseEnv = {
        attempt: sendAttemptRef.current,
        stage,
        connectedWallet,
        walletAddress,
        walletChain,
        selectedNetwork: selectedNetwork
          ? { name: selectedNetwork.name, chainId: selectedNetwork.chainId }
          : null,
        isPhantomEVM,
        isWalletInitializing,
        location:
          typeof window !== 'undefined'
            ? { href: window.location.href, host: window.location.host }
            : null,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
        providerMeta: metamaskProvider
          ? {
              hasRequest: typeof metamaskProvider.request === 'function',
              isMetaMask: !!metamaskProvider.isMetaMask,
              isWalletConnect: !!metamaskProvider.wc,
              isPhantom: !!metamaskProvider.isPhantom,
              constructorName: metamaskProvider.constructor?.name || null,
            }
          : null,
      };
      devLog(
        `[TX_MON] ${now} ${stage}`,
        extra ? { ...baseEnv, ...extra } : baseEnv,
      );
    },
    [connectedWallet, walletAddress, walletChain, selectedNetwork, isPhantomEVM, isWalletInitializing, metamaskProvider, devLog],
  );

  /** Cooldown (ms) após falha/rejeição para evitar loop do prompt da carteira */
  const TRANSFER_COOLDOWN_MS = 20000;
  /** Timeout para aguardar receipt via RPC público (após envio da tx) */
  const AWAIT_RECEIPT_TIMEOUT_MS = 90000;
  /** Timeout base para envio da transação (transfer/sendTransaction) */
  const SEND_TX_TIMEOUT_MS = 45000;
  const POLL_RECEIPT_INTERVAL_MS = 5000;
  const POLL_RECEIPT_MAX_ATTEMPTS = 48; // 4 min no handleSubmit; no mobile o usuário pode demorar na carteira
  const { hasPendingFees, isOverLimit, totalPendingUSD, addPendingFee } = usePendingFees();

  // Lista de stablecoins configuradas para a rede atual (modo Enviar),
  // sempre exibindo os tokens suportados, mesmo com saldo 0.
  const availableTokens = useMemo(() => {
    const chainId = selectedNetwork?.chainId;
    const contracts = chainId ? STABLECOIN_CONTRACTS[chainId] : null;
    if (!contracts) return [];
    return Object.keys(contracts).map((symbol) => {
      const raw = walletStablecoinBalances?.[symbol];
      const balance = raw != null ? parseFloat(raw) : 0;
      return { symbol, balance };
    });
  }, [selectedNetwork?.chainId, walletStablecoinBalances]);

  // Lista de tokens da rede para receber (modo Receber — não exige saldo)
  const tokensForCobranca = useMemo(() => {
    const chainId = selectedNetwork?.chainId;
    const contracts = chainId ? STABLECOIN_CONTRACTS[chainId] : null;
    if (!contracts) return [];
    return Object.keys(contracts).map((symbol) => ({ symbol, balance: null }));
  }, [selectedNetwork?.chainId]);

  // Tokens exibidos: Enviar = com saldo; Cobrança = todos da rede
  const tokensToShow = transferMode === 'cobranca' ? tokensForCobranca : availableTokens;

  // Selecionar primeiro token quando lista muda ou token atual não está na lista (ex.: modo Cobrança)
  useEffect(() => {
    if (tokensToShow.length === 0) return;
    const currentInList = formData.selectedToken && tokensToShow.some((t) => t.symbol === formData.selectedToken);
    if (!formData.selectedToken || !currentInList) {
      setFormData(prev => ({ ...prev, selectedToken: tokensToShow[0].symbol }));
    }
  }, [tokensToShow, formData.selectedToken]);

  // Token selecionado atualmente (para Enviar: dados de saldo; para Cobrança: só símbolo)
  const selectedTokenData = useMemo(() => {
    return transferMode === 'cobranca'
      ? tokensForCobranca.find(t => t.symbol === formData.selectedToken) || null
      : availableTokens.find(t => t.symbol === formData.selectedToken) || null;
  }, [transferMode, availableTokens, tokensForCobranca, formData.selectedToken]);

  // Cálculos de valores (formData.amount = string do input, pode ser "10," ou "1.234,56")
  const amountNum = parseAmountFromInput(formData.amount, formData.currency) || 0;
  const transferFee = calculateFee(amountNum);                       // taxa de transferência
  const billingFee = transferMode === 'cobranca' ? calculateBillingFee(amountNum) : 0; // taxa de recebimento (só no modo Receber)
  const totalFee = transferFee + billingFee;                         // taxa total (somatório)

  // No modo Receber: taxa de recebimento sempre paga pelo cobrador; taxa de transferência: se absorbTransferFee o cobrador paga, senão o pagador paga (embutida)
  const embeddedFee = transferMode === 'cobranca' && !absorbTransferFee ? transferFee : 0; // só a taxa de transferência pode ser embutida
  const fee = transferMode === 'cobranca' ? embeddedFee : transferFee;
  const billerPaysFee = transferMode === 'cobranca' ? billingFee + (absorbTransferFee ? transferFee : 0) : 0; // cobrador sempre paga billing + transfer se absorver
  const totalInSelectedCurrency = amountNum + fee;
  
  // Conversão para a outra moeda
  const otherCurrency = formData.currency === 'USD' ? 'BRL' : 'USD';
  const convertedTotal = convert(totalInSelectedCurrency, formData.currency, otherCurrency);
  const convertedAmount = convert(amountNum, formData.currency, otherCurrency);
  const convertedFee = convert(fee, formData.currency, otherCurrency);

  // Valor em USD (para comparar com saldo da stablecoin)
  const amountInUSD = formData.currency === 'USD' ? amountNum : convert(amountNum, 'BRL', 'USD');
  const feeInUSD = formData.currency === 'USD' ? fee : convert(fee, 'BRL', 'USD');
  const totalInUSD = formData.currency === 'USD' ? totalInSelectedCurrency : convert(totalInSelectedCurrency, 'BRL', 'USD');
  
  // Valor líquido que vai para o destinatário (em USD, para envio)
  const netAmountInUSD = amountInUSD;

  // Validações
  // Considera carteiras EVM conectadas: MetaMask (injetado) e WalletConnect (multi-wallet)
  const isWalletConnected =
    connectedWallet === 'metamask' ||
    connectedWallet === 'walletconnect' ||
    (connectedWallet === 'phantom' && isPhantomEVM);
  // Tokens configurados para a rede atual
  const hasTokens = availableTokens.length > 0;
  // Alguma stablecoin com saldo > 0
  const hasAnyStablecoinBalance = availableTokens.some((t) => t.balance > 0);
  const hasValidRecipient = formData.recipient && /^0x[a-fA-F0-9]{40}$/.test(formData.recipient);
  const meetsMinAmount = minSendChargeAmount <= 0 || amountInUSD >= minSendChargeAmount;
  const hasValidAmount = amountNum > 0 && meetsMinAmount;
  // Mensagens de valor mínimo: na moeda de exibição atual e, se não for USD, entre parênteses o valor em USD
  const minAmountDisplay =
    minSendChargeAmount <= 0
      ? ''
      : formData.currency === 'USD'
        ? formatCurrencyDisplay(minSendChargeAmount, 'USD')
        : `${formatCurrencyDisplay(convert(minSendChargeAmount, 'USD', formData.currency), formData.currency)} (${formatCurrencyDisplay(minSendChargeAmount, 'USD')} USD)`;
  // No modo Enviar enviamos só o líquido; a taxa vira pendente — saldo precisa cobrir só o líquido
  const requiredAmountUSD = transferMode === 'enviar' ? netAmountInUSD : totalInUSD;
  const hasSufficientBalance = selectedTokenData && requiredAmountUSD <= selectedTokenData.balance;
  const canSubmit =
    isWalletConnected &&
    hasValidRecipient &&
    hasValidAmount &&
    !!selectedTokenData &&
    !isSubmitting;
  const canGenerateQR = isWalletConnected && hasValidAmount && formData.selectedToken && walletAddress;

  // Admin atual (para recursos de diagnóstico em tempo de execução)
  const currentUser = getCurrentUser();
  const { isCurrentUserAdmin: isAdminUser } = useAdmin();
  const formValidationErrorPreview = getTransferFormValidationError({
    hasValidRecipient,
    hasValidAmount,
    hasTokens,
    selectedTokenData,
    hasSufficientBalance,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'currency') {
      const num = parseAmountFromInput(formData.amount, formData.currency) || 0;
      const newNum = convertAmountOnCurrencyChange
        ? convert(num, formData.currency, value)
        : num;
    setFormData(prev => ({
      ...prev,
        currency: value,
        amount: num > 0 || formData.amount !== '' ? formatAmountForBlur(newNum, value) : '',
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setTxHash(null);
  };

  const handleCurrencySelect = (value) => {
    if (value !== 'USD' && value !== 'BRL') return;
    const num = parseAmountFromInput(formData.amount, formData.currency) || 0;
    const newNum = convertAmountOnCurrencyChange
      ? convert(num, formData.currency, value)
      : num;
    updateSettings?.('displayCurrency', value);
    setFormData(prev => ({
      ...prev,
      currency: value,
      amount: num > 0 || formData.amount !== '' ? formatAmountForBlur(newNum, value) : '',
    }));
    setTxHash(null);
    setCurrencyDropdownOpen(false);
  };

  useEffect(() => {
    if (!currencyDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target)) {
        setCurrencyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [currencyDropdownOpen]);

  // Quando moeda está travada, manter formData.currency alinhado à configuração (Enviar/Receber não mostram seletor).
  useEffect(() => {
    if (lockDisplayCurrency && settingsDisplayCurrency) {
      const target = settingsDisplayCurrency === 'BRL' ? 'BRL' : 'USD';
      setFormData((prev) => (prev.currency !== target ? { ...prev, currency: target } : prev));
    }
  }, [lockDisplayCurrency, settingsDisplayCurrency]);

  useEffect(() => {
    const saved = sessionStorage.getItem('pendingTransfer');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Verifica se ainda não foi processado e se o txHash corresponde
        if (!transferSuccessAppliedRef.current && txHash && parsed.chainId) {
          pendingTransferRef.current = parsed;
        } else {
          // Se já processou ou não tem txHash, limpa o storage
          sessionStorage.removeItem('pendingTransfer');
        }
      } catch (e) {
        devLog('[TRANSFER] erro ao restaurar pendingTransfer', e);
      }
    }
  }, [txHash]); // depende de txHash para saber se há transação pendente

  const handleAmountChange = (e) => {
    const raw = e.target.value;
    const sanitized = sanitizeAmountInput(raw, formData.currency);
    setFormData(prev => ({ ...prev, amount: sanitized }));
    setTxHash(null);
  };

  /** Formata o valor ao sair do campo: separador e milhares conforme a moeda (USD = . / BRL = ,) */
  const handleAmountBlur = () => {
    const raw = formData.amount.trim();
    if (raw === '') {
      setFormData(prev => ({ ...prev, amount: '' }));
      return;
    }
    const parsed = parseAmountFromInput(raw, formData.currency);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      setFormData(prev => ({ ...prev, amount: formatAmountForBlur(parsed, formData.currency) }));
    }
    setTxHash(null);
  };

  const handlePostConfirmation = useCallback(async ({
    receipt,
    pending,
    txHash,
    tokenAddress,
    chainId,
  }) => {
    try {
      // Evitar execução duplicada
      if (transferSuccessAppliedRef.current) return;
      transferSuccessAppliedRef.current = true;
      pendingTransferRef.current = null;

      sessionStorage.removeItem('pendingTransfer');

      setTransferConfirmed(true);
      setLastConfirmedDetail({
        at: Date.now(),
        amount: pending.sendAmountUSD,
        recipient: pending.recipient,
        token: pending.token,
      });
      refreshWalletBalances();

      // 🔔 NOTIFICAÇÕES – Agora unificadas!
      if (enableFirestoreNotifications && enableCreateNotificationOnSend) {
        try {
          await createTransferNotification({
            from: walletAddress,
            to: pending.recipient,
            txHash,
            amount: pending.sendAmountUSD,
            token: pending.token,
            tokenAddress,
            network: chainId,
            networkName: pending.networkName,
            sentBy: currentUser?.email || '',
          });
        } catch (err) {
          console.error('[Transfer] Erro ao criar notificação Firestore:', err);
        }
      }

      try {
        addNotification({
          type: 'sent',
          txHash,
          amount: pending.sendAmountUSD,
          symbol: pending.token,
          networkName: pending.networkName,
          chainId,
          toAddress: pending.recipient,
        });
      } catch (err) {
        // não bloqueia
      }

      // Taxas pendentes (já existia)
      if (pending.hasFee && typeof addPendingFee === 'function') {
        try {
          addPendingFee({
            feeUSD: pending.feeInUSD,
            token: pending.token,
            chainId,
            networkName: pending.networkName,
            originTxHash: txHash,
          });
          removePendingTxReconcile(txHash);
        } catch (err) {
          console.error('[Transfer] Erro ao registrar taxa pendente:', err);
        }
      }

      // Toast de sucesso
      success(
        pending.hasFee
          ? t('transfer.txSuccessWithFee', {
            amount: formatCurrencyDisplay(pending.sendAmountUSD, 'USD'),
            token: pending.token,
            fee: formatCurrencyDisplay(pending.feeInUSD, 'USD'),
          })
          : t('transfer.txSuccess', {
            amount: formatCurrencyDisplay(pending.sendAmountUSD, 'USD'),
            token: pending.token,
          })
      );

      // Limpar formulário
      setFormData(prev => ({ ...prev, recipient: '', amount: '' }));
      setIsSubmitting(false);
      setTransferStep(null);
    } catch (err) {
      devLog('[POST_CONFIRMATION] erro', { error: err.message, stack: err.stack });
    }
  }, [
    walletAddress,
    refreshWalletBalances,
    enableFirestoreNotifications,
    enableCreateNotificationOnSend,
    currentUser,
    addNotification,
    addPendingFee,
    removePendingTxReconcile,
    success,
    t,
  ]);

  // Ao voltar à aba: se tiver txHash e transferência pendente (com ou sem isSubmitting), buscar receipt.
  // No mobile o usuário sai para a carteira; ao voltar podemos concluir mesmo se o handleSubmit já desistiu.
  const visibilityPollRef = useRef(null);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        if (visibilityPollRef.current) {
          clearInterval(visibilityPollRef.current);
          visibilityPollRef.current = null;
        }
        return;
      }
      if (!txHash || transferSuccessAppliedRef.current || !pendingTransferRef.current) return;
      const pending = pendingTransferRef.current;
      if (!pending?.chainId) return;
      const readOnlyProvider = getReadOnlyProvider(pending.chainId);
      let cancelled = false;
      const poll = async () => {
        if (cancelled) return;
        try {
          const receipt = await readOnlyProvider.getTransactionReceipt(txHash);
          if (cancelled) return;
          if (receipt && receipt.status === 1) {
            const pending = pendingTransferRef.current;
            if (!pending || transferSuccessAppliedRef.current) return;

            // Obtém o endereço do contrato do token (necessário para a notificação)
            const contracts = STABLECOIN_CONTRACTS[pending.chainId];
            const tokenAddress = contracts?.[pending.token] || '';

            // Chama a função unificada de pós-confirmação
            await handlePostConfirmation({
              receipt,
              pending,
              txHash,
              tokenAddress,
              chainId: pending.chainId,
            });

            if (visibilityPollRef.current) {
              clearInterval(visibilityPollRef.current);
              visibilityPollRef.current = null;
            }
          }
        } catch (err) {
          devLog('[VISIBILITY] erro no polling', { error: err.message });
        }
      };
      visibilityPollRef.current = setInterval(poll, POLL_RECEIPT_INTERVAL_MS);
      poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (visibilityPollRef.current) {
        clearInterval(visibilityPollRef.current);
        visibilityPollRef.current = null;
      }
    };
  }, [txHash, handlePostConfirmation]); // <-- adicionada handlePostConfirmation como dependência

  // Atualizar contagem regressiva do cooldown a cada segundo (para exibir "Aguarde Xs")
  useEffect(() => {
    if (transferCooldownUntil <= Date.now()) {
      setCooldownSecondsLeft(0);
      return;
    }
    const update = () => {
      const left = Math.max(0, Math.ceil((transferCooldownUntil - Date.now()) / 1000));
      setCooldownSecondsLeft(left);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [transferCooldownUntil]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (walletPickerRef.current && !walletPickerRef.current.contains(e.target)) {
        setShowWalletPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ao receber pagamento na blockchain: fechar tela do QR (modal e inline)
  useEffect(() => {
    if (incomingTransfer) {
      setShowQRModal(false);
      setShowQRInline(false);
      setPaymentRequestURL('');
      setPaymentRequestPayload('');
    }
  }, [incomingTransfer]);

  const selectRecipientFromWallet = (address) => {
    setFormData(prev => ({ ...prev, recipient: address }));
    setIsRecipientFromList(true); // Marca que o endereço veio da lista
    setShowWalletPicker(false);
  };

  // Limpa o endereço do destinatário
  const clearRecipient = () => {
    setFormData(prev => ({ ...prev, recipient: '' }));
    setIsRecipientFromList(false);
    setTxHash(null);
  };

  // URL para receber com token opaco (id do Firestore) — endereço e dados não vão na URL
  const buildPaymentRequestURLFromId = useCallback((id) => {
    const publicOrigin = (import.meta.env?.VITE_APP_PUBLIC_ORIGIN || '').trim();
    const origin = publicOrigin || window.location.origin;
    const base = origin.replace(/\/+$/, '') + '/';
    return `${base}?pay=1&id=${encodeURIComponent(id)}`;
  }, []);

  // Aplica payload de recebimento ao formulário (token opaco ou legacy)
  const applyPaymentPayload = useCallback(
    (payload, source) => {
      const { to, amount, token, currency, network: networkChainId, receiverProfile } = payload;
      setFormData((prev) => ({
        ...prev,
        recipient: to || prev.recipient,
        amount: amount != null ? formatAmountForInput(Number(amount), currency === 'BRL' ? 'BRL' : 'USD') : prev.amount,
        selectedToken: token || prev.selectedToken,
        currency: currency === 'BRL' ? 'BRL' : 'USD',
      }));
      setIsRecipientFromList(source === 'qr');
      if (receiverProfile && typeof receiverProfile === 'object') {
        setReceiverProfileFromPaymentRequest(receiverProfile);
      } else {
        setReceiverProfileFromPaymentRequest(null);
      }
      if (networkChainId && supportedNetworks?.length) {
        const network = supportedNetworks.find((n) => n.chainId === networkChainId);
        if (network) switchNetwork(network);
      }
      logQRParamsResult(true, { toPresent: !!to, amount, token: token || null, networkChainId: networkChainId || null });
    },
    [supportedNetworks, switchNetwork],
  );

  // Ao abrir link para receber: resolve token opaco (id) ou usa parâmetros legacy (to, amount, ...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pay') !== '1') return;

    const id = params.get('id');

    if (id) {
      getPaymentRequest(id)
        .then((payload) => {
          if (payload) {
            applyPaymentPayload(payload, 'url');
          } else {
            logQRParamsResult(false, { reason: 'token_not_found_or_expired', id: id.substring(0, 8) });
          }
        })
        .catch((err) => {
          logQRParamsResult(false, { reason: 'resolve_error', errorMessage: err?.message ?? String(err) });
        })
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
        });
      return;
    }

    const to = params.get('to');
    const amount = params.get('amount');
    const token = params.get('token');
    const currency = params.get('currency') || 'USD';
    const networkChainId = params.get('network');

    if (!to || !amount) {
      logQRParamsResult(false, { reason: 'missing_to_or_amount', hasTo: !!to, hasAmount: !!amount });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    try {
      applyPaymentPayload({ to, amount, token, currency, network: networkChainId }, 'url');
    } catch (err) {
      devLog('[PaymentRequest] Error applying payment payload', { errorMessage: err?.message, stack: err?.stack });
      logQRParamsResult(false, { reason: 'apply_error', errorMessage: err?.message ?? String(err) });
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [applyPaymentPayload]);

  // Processa QR lido pelo scanner (modo Enviar): token opaco, URL legacy, JSON legacy ou endereço
  const handleQRScanned = useCallback(
    async (decodedText) => {
      try {
        const raw = decodedText.trim();

        // 1) URL para receber com token opaco (pay=1&id=...)
        if (raw.includes('?') && raw.includes('pay=1')) {
          try {
            const url = new URL(raw);
            const id = url.searchParams.get('id');
            if (id) {
              const payload = await getPaymentRequest(id);
              if (payload) {
                applyPaymentPayload(payload, 'qr');
                success(t('transfer.qrReadSuccess'));
                return;
              }
            }
            const to = url.searchParams.get('to');
            const amount = url.searchParams.get('amount');
            if (to && /^0x[a-fA-F0-9]{40}$/.test(to) && amount) {
              applyPaymentPayload(
                {
                  to,
                  amount,
                  token: url.searchParams.get('token') || '',
                  currency: url.searchParams.get('currency') || 'USD',
                  network: url.searchParams.get('network') || '',
                },
                'qr',
              );
              success(t('transfer.qrReadSuccess'));
              return;
            }
          } catch (err) {
            devLog('[QRScanner] Error parsing URL', { raw, errorMessage: err?.message, stack: err?.stack });
          }
        }

        // 2) JSON: token opaco { pay: 1, id: "..." } ou legacy { to, amount, ... }
        if (raw.startsWith('{')) {
          let obj;
          try {
            obj = JSON.parse(raw);
          } catch (err) {
            devLog('[QRScanner] Error parsing JSON', { raw, errorMessage: err?.message });
          }
          if (obj && typeof obj === 'object') {
            if (obj.id && (obj.pay === 1 || obj.pay === '1')) {
              const payload = await getPaymentRequest(obj.id);
              if (payload) {
                applyPaymentPayload(payload, 'qr');
                success(t('transfer.qrReadSuccess'));
                return;
              }
            }
            if (obj.to && /^0x[a-fA-F0-9]{40}$/.test(String(obj.to))) {
              const amount = obj.amount != null ? String(obj.amount) : '';
              const token = obj.token || '';
              const currency = obj.currency === 'BRL' ? 'BRL' : 'USD';
              const networkChainId = obj.network || '';
              applyPaymentPayload(
                { to: String(obj.to), amount, token, currency, network: networkChainId },
                'qr',
              );
              success(t('transfer.qrReadSuccess'));
              return;
            }
          }
        }

        // 3) Endereço simples (0x...)
        const addressMatch = raw.match(/0x[a-fA-F0-9]{40}/);
        if (addressMatch) {
          setFormData((prev) => ({ ...prev, recipient: addressMatch[0] }));
          setIsRecipientFromList(true);
          success(t('transfer.addressReadSuccess'));
          return;
        }

        showError(t('transfer.qrNotRecognized'));
      } catch (err) {
        devLog('[QRScanner] Error processing QR', { errorMessage: err?.message, stack: err?.stack });
        showError(t('transfer.qrProcessError'));
      }
    },
    [applyPaymentPayload, success, showError],
  );

  const handleWalletQRScanned = useCallback(
    (raw) => {
      if (!raw?.trim() || !onOpenCarteiras) return;
      try {
        const obj = JSON.parse(raw.trim());
        if (obj && typeof obj === 'object' && obj.type === 'wallet' && obj.address && /^0x[a-fA-F0-9]{40}$/.test(String(obj.address))) {
          const payload = {
            address: String(obj.address).trim(),
            name: obj.name || '',
            email: obj.email || '',
            phone: obj.phone || '',
            network: obj.network,
            networkName: obj.networkName || '',
            photoURL: obj.photoURL,
            tags: Array.isArray(obj.tags) ? obj.tags : undefined,
          };
          setShowWalletQRScanner(false);
          setFormData((prev) => ({ ...prev, recipient: payload.address }));
          setRecipientWalletPayload(payload);
          onOpenCarteiras(payload.address, payload);
          success(t('transfer.walletQRReadSuccess'));
          return;
        }
      } catch (err) {
        devLog('[WalletQRScanner] Error processing wallet QR', { errorMessage: err?.message, stack: err?.stack });
      }
      showError(t('transfer.walletQRNotWallet'));
    },
    [onOpenCarteiras, success, showError],
  );

  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const handleGenerateQR = async () => {
    if (isOverLimit) {
      onOpenPendingFeeModal?.('blocking');
      return;
    }
    setGeneratingPaymentLink(true);
    try {
      const fromRecord = activeWalletRecord;
      const fromUser = user;
      const phone = includePhoneInWalletQR ? ((user?.uid && getStoredGooglePhone(user.uid)) || fromRecord?.phone || '').trim() : '';
      const receiverProfile = {
        name: includeNameInWalletQR ? (fromRecord?.name || fromUser?.name || fromUser?.displayName || '') : '',
        email: includeEmailInWalletQR ? (fromRecord?.email || fromUser?.email || '') : '',
        phone: phone || '',
        photoURL: includePhotoInWalletQR ? (fromUser?.photoURL || fromRecord?.photoURL || '') : '',
        network: includeNetworkInWalletQR && selectedNetwork ? selectedNetwork.chainId : '',
        networkName: includeNetworkInWalletQR && selectedNetwork ? (selectedNetwork.name || '') : '',
      };
      const id = await createPaymentRequest({
        to: walletAddress || '',
        amount: String(amountNum),
        token: formData.selectedToken || '',
        network: selectedNetwork?.chainId || '',
        currency: formData.currency || 'USD',
        receiverProfile,
      });
      const url = buildPaymentRequestURLFromId(id);
      setPaymentRequestURL(url);
      setPaymentRequestPayload(url);
      setShowQRModal(true);
      setShowQRInline(false);
      try {
        localStorage.setItem('pcp_last_biller_absorb', JSON.stringify({ absorbTransferFee: !!absorbTransferFee }));
      } catch { /* ignore */ }
    } catch (err) {
      devLog('[Receber] Error creating payment request', { errorMessage: err?.message, stack: err?.stack });
      showError(err?.message || t('transfer.linkGenerateError'));
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  const copyPaymentLink = async () => {
    if (!paymentRequestURL) return;
    try {
      await navigator.clipboard.writeText(paymentRequestURL);
      success(t('transfer.linkCopied'));
    } catch (err) {
      devLog('[TransferForm] Error copying payment link', { errorMessage: err?.message, stack: err?.stack });
      showError(t('transfer.copyError'));
    }
  };

  const copyChargeAddress = async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      success(t('transfer.addressCopied'));
      setChargeAddressCopyFeedback(true);
      if (chargeAddressCopyFeedbackTimeoutRef.current) clearTimeout(chargeAddressCopyFeedbackTimeoutRef.current);
      chargeAddressCopyFeedbackTimeoutRef.current = setTimeout(() => {
        setChargeAddressCopyFeedback(false);
        chargeAddressCopyFeedbackTimeoutRef.current = null;
      }, 2000);
    } catch {
      devLog('[TransferForm] Error copying charge address', { errorMessage: err?.message, stack: err?.stack });
      showError(t('transfer.copyError'));
    }
  };

  // Enviar transação (uma tx: líquido; taxa vira pendente)
  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    // Incrementa contador de tentativa e registra snapshot completo do ambiente
    sendAttemptRef.current += 1;
    txLog('handleSubmit:start', {
      transferMode,
      amountNum,
      feeInUSD,
      selectedToken: formData.selectedToken,
      recipient: formData.recipient,
      cooldownSecondsLeft,
      isOverLimit,
      hasPendingFees,
      hasValidRecipient,
      hasValidAmount,
      hasTokens,
      hasSufficientBalance,
      isSubmittingBeforeClick: isSubmitting,
    });

    if (isSubmitting) return;
    const now = Date.now();
    if (transferCooldownUntil > now) {
      const sec = Math.ceil((transferCooldownUntil - now) / 1000);
      showError(t('transfer.waitBeforeRetry', { seconds: sec }));
      txLog('handleSubmit:cooldown_block', { secondsLeft: sec });
      return;
    }
    if (isOverLimit) {
      onOpenPendingFeeModal?.('blocking');
      txLog('handleSubmit:blocked_by_pending_fees', {});
      return;
    }
    if (!isWalletConnected) {
      showError(t('transfer.connectWalletToSendError'));
      return;
    }
    if (!metamaskProvider || typeof metamaskProvider.request !== 'function') {
      showError(t('transfer.walletUnavailable'));
      txLog('handleSubmit:wallet_provider_unavailable', {});
      return;
    }
    // Verifica se a rede da carteira bate com a rede selecionada na aplicação
    let walletChainId = normalizeChainId(walletChain);
    const selectedChainId = normalizeChainId(selectedNetwork?.chainId);
    const appNetworkName = selectedNetwork?.name || selectedChainId;

    if (walletChainId && selectedChainId && isNetworkMismatch(walletChainId, selectedChainId)) {
      // Leitura fresca: a carteira pode já estar na rede certa (ex.: usuário trocou no MetaMask, chainChanged não disparou)
      try {
        const currentChainRaw = await metamaskProvider.request({ method: 'eth_chainId' });
        const effective = getEffectiveWalletChainId(walletChainId, currentChainRaw);
        if (effective) walletChainId = effective;
        if (!isNetworkMismatch(walletChainId, selectedChainId)) {
          txLog('handleSubmit:network_synced_fresh', { previousState: walletChain, currentChain: walletChainId });
        }
      } catch (err) {
        devLog('[TransferForm] Error getting effective wallet chain id', { errorMessage: err?.message, stack: err?.stack });
        // mantém walletChainId do estado
      }

      if (isNetworkMismatch(walletChainId, selectedChainId)) {
        const walletNetworkNameInitial = getChainDisplayName
          ? getChainDisplayName(walletChain)
          : walletChainId;

        txLog('handleSubmit:network_mismatch_detected', {
          walletChain: walletChainId,
          selectedChain: selectedChainId,
          walletNetworkName: walletNetworkNameInitial,
          appNetworkName,
        });

        // Regra geral da aplicação: sempre que possível, sincronizar automaticamente.
      // Aqui, tentamos primeiro trocar a rede da carteira para a rede selecionada na aplicação.
      if (metamaskProvider && typeof metamaskProvider.request === 'function') {
        try {
          // 1) Tenta trocar de rede via wallet_switchEthereumChain
          await metamaskProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: selectedChainId }],
          });

          const newChain = await metamaskProvider.request({ method: 'eth_chainId' }).catch(() => null);
          const normalizedNewChain = newChain ? String(newChain).toLowerCase() : null;
          if (normalizedNewChain) {
            walletChainId = normalizedNewChain;
          }

          txLog('handleSubmit:network_switched_auto', {
            fromChain: walletChain,
            toChain: normalizedNewChain,
            expectedChain: selectedChainId,
          });
        } catch (switchErr) {
          const code = switchErr?.code;

          // 2) Se a rede não estiver adicionada à carteira (código 4902), tenta adicionar e trocar
          const params = NETWORK_PARAMS[selectedChainId];
          if ((code === 4902 || code === '4902') && params) {
            try {
              await metamaskProvider.request({
                method: 'wallet_addEthereumChain',
                params: [params],
              });

              const newChain = await metamaskProvider.request({ method: 'eth_chainId' }).catch(() => null);
              const normalizedNewChain = newChain ? String(newChain).toLowerCase() : null;
              if (normalizedNewChain) {
                walletChainId = normalizedNewChain;
              }

              txLog('handleSubmit:network_added_and_switched_auto', {
                fromChain: walletChain,
                toChain: normalizedNewChain,
                expectedChain: selectedChainId,
              });
            } catch (addErr) {
              txLog('handleSubmit:network_switch_failed', {
                errorCode: addErr?.code,
                errorMessage: addErr?.message || String(addErr),
              });
            }
          } else {
            txLog('handleSubmit:network_switch_failed', {
              errorCode: switchErr?.code,
              errorMessage: switchErr?.message || String(switchErr),
            });
          }
        }
      }

      // Revalida após tentativa de sincronização automática
      if (isNetworkMismatch(walletChainId, selectedChainId)) {
        const walletNetworkName = getChainDisplayName
          ? getChainDisplayName(walletChain)
          : walletChainId;

        showError(t('transfer.networkMismatch', {
          walletNetwork: walletNetworkName || walletChainId,
          appNetwork: appNetworkName,
        }));
        txLog('handleSubmit:network_mismatch', {
          walletChain: walletChainId,
          selectedChain: selectedChainId,
          walletNetworkName,
          appNetworkName,
        });
        return;
      }
      }
    }
    const validationError = getTransferFormValidationError({
      hasValidRecipient,
      hasValidAmount,
      hasTokens,
      selectedTokenData,
      hasSufficientBalance,
    });

    if (validationError) {
      if (validationError === 'invalid_recipient') {
        showError(t('transfer.validRecipientRequired'));
        txLog('handleSubmit:invalid_recipient', { recipient: formData.recipient });
      } else if (validationError === 'invalid_amount') {
        showError(t('transfer.amountGreaterThanZero'));
        txLog('handleSubmit:invalid_amount', { amountNum });
      } else if (validationError === 'no_tokens') {
        showError(t('transfer.noStablecoinSupported'));
        txLog('handleSubmit:no_tokens_available', { hasTokens, selectedTokenData });
      } else if (validationError === 'insufficient_balance') {
        showError(t('transfer.insufficientBalanceForValueFee'));
        txLog('handleSubmit:insufficient_balance', {
          requiredAmountUSD,
          selectedTokenBalance: selectedTokenData?.balance ?? null,
        });
      }
      return;
    }
    transferSuccessAppliedRef.current = false;
    pendingTransferRef.current = null;
    setIsSubmitting(true);
    setTransferStep('recipient');
    info(t('transfer.sendingStarted'));
    setTxHash(null);
    setFeesTxHash(null);
    setTransferConfirmed(false);
    setLastConfirmedDetail(null);
    setRecipientWalletPayload(null);
    setReceiverProfileFromPaymentRequest(null);
    setFeeConfirmed(false);

    try {
      const chainId = selectedNetwork?.chainId;
      const contracts = STABLECOIN_CONTRACTS[chainId];

      if (!contracts || !contracts[formData.selectedToken]) {
        throw new Error(`Contrato não encontrado para ${formData.selectedToken} na rede ${selectedNetwork?.name}`);
      }

      const tokenAddress = contracts[formData.selectedToken];

      info(t('transfer.checkingNetwork'));
      txLog('handleSubmit:env_ready', {
        chainId,
        tokenAddress,
      });

      const hasFee = IS_FEE_ENABLED && feeInUSD > 0 && getFeeAddress(chainId);
      const sendAmountUSD = netAmountInUSD;

      setTransferStep('recipient');
      info(t('transfer.sendingTransfer'));
      
      const amountStr = String(sendAmountUSD);
      // Um único fluxo igual ao completao.jsx: BrowserProvider → getSigner → Contract → decimals → transfer.
      const tx = await sendStablecoinTransferCompletao(
        metamaskProvider,
        tokenAddress,
        formData.recipient,
        amountStr,
        connectedWallet === 'walletconnect' ? undefined : chainId,
      );


      const txHash = tx?.hash;
      if (!txHash) {
        throw new Error('Não foi possível obter o hash da transação.');
      }

      setTxHash(txHash);
      setTransferStep(null); // deixa de mostrar "Enviando para destinatário"; só "aguardando confirmação"
      
      // Armazenamos também o tokenAddress para uso posterior na notificação
      pendingTransferRef.current = {
        sendAmountUSD,
        recipient: formData.recipient || '',
        token: formData.selectedToken || '',
        hasFee,
        feeInUSD,
        networkName: selectedNetwork?.name ?? '',
        chainId: selectedNetwork?.chainId ?? '',
        tokenAddress, // <-- adicionado
      };


      sessionStorage.setItem('pendingTransfer', JSON.stringify(pendingTransferRef.current));

      if (hasFee) {
        addPendingTxReconcile({
          txHash,
          chainId: selectedNetwork?.chainId,
          feeUSD: feeInUSD,
          token: formData.selectedToken,
          networkName: selectedNetwork?.name ?? '',
          createdAt: Date.now(),
        });
      }

      info(t('transfer.awaitingConfirmation'));

      let receipt;
      try {
        // Primeiro tenta aguardar via tx.wait() (provider da carteira),
        // com timeout para não travar no mobile.
        receipt = await Promise.race([
          tx.wait(),
          new Promise((_, rej) =>
            setTimeout(() => rej(new Error('TIMEOUT_RECEIPT')), AWAIT_RECEIPT_TIMEOUT_MS),
          ),
        ]);
      } catch (waitErr) {
        devLog('[TRANSFER] ERRO no handleSubmit (waitErr)', {
          code: waitErr?.code,
          message: waitErr?.message,
          stack: waitErr?.stack,
        });
        if (waitErr?.message === 'TIMEOUT_RECEIPT') {
          mmLog('TransferForm: tx.wait() timeout — iniciando polling do receipt (RPC público)', {
            txHash,
          });
          const readOnlyProvider = getReadOnlyProvider(selectedNetwork?.chainId || chainId);
          for (let i = 0; i < POLL_RECEIPT_MAX_ATTEMPTS; i++) {
            await new Promise((r) => setTimeout(r, POLL_RECEIPT_INTERVAL_MS));
            try {
              const r = await readOnlyProvider.getTransactionReceipt(txHash);
              if (r && r.status === 1) {
                receipt = r;
                break;
              }
            } catch {
              // continua tentando
            }
          }
          if (!receipt || receipt.status !== 1) {
            setTransferStep(null);
            setIsSubmitting(false);
            info(t('transfer.checkExplorerOrWallet'));
            mmLog('TransferForm: polling encerrado sem receipt (RPC público)', { txHash });
            txLog('handleSubmit:receipt_not_found', { txHash });
            return;
          }
        } else {
          throw waitErr;
        }
      }

      if (!receipt || receipt.status !== 1) {
        removePendingTxReconcile(txHash);
        throw new Error('Transferência falhou');
      }

      // Se já foi processado (pode ocorrer se o polling de visibilidade já tiver sido executado), não duplicar
      if (transferSuccessAppliedRef.current) {
        setTransferStep(null);
        setIsSubmitting(false);
        return;
      }

      // Chama a função unificada de pós-confirmação
      await handlePostConfirmation({
        receipt,
        pending: pendingTransferRef.current,
        txHash,
        tokenAddress,
        chainId,
      });

    } catch (err) {
      mmLog(`TransferForm: erro no envio code=${err?.code} message=${err?.message || String(err)}`);
      txLog('handleSubmit:error', {
        errorCode: err?.code,
        errorMessage: err?.message || String(err),
        name: err?.name || null,
      });
      console.error('[Transfer] Erro:', err?.code, err?.message, err);
      setTransferStep(null);
      setIsSubmitting(false);
      setTransferCooldownUntil(Date.now() + TRANSFER_COOLDOWN_MS);
      if (err?.code === 'ACTION_REJECTED' || err?.code === 4001) {
        showError(t('transfer.txCancelled'));
      } else if (err?.message?.includes('insufficient funds')) {
        showError(t('transfer.insufficientGas'));
      } else if (err?.message?.includes('Failed to fetch') || (err?.code === 'UNKNOWN_ERROR' && err?.message?.includes('-32000'))) {
        showError(t('transfer.networkErrorRetry'));
        info(t('transfer.checkExplorerOrWallet'));
      } else {
        showError(err?.message || t('transfer.transferError'));
      }
    }
  };

  // Handler do botão principal; log em cada passo para diagnóstico no mobile
  const handleSubmitButtonClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Verifica se acúmulo de taxa pendente atingiu o limite
    if (isOverLimit) {
      onOpenPendingFeeModal?.('blocking');
      return;
    }

    if (transferMode === 'cobranca') {
      if (!canGenerateQR) {
        if (!isWalletConnected) { return showError(t('transfer.connectWalletToCharge')); }
        if (amountNum <= 0) { return showError(t('transfer.amountGreaterThanZero')); }
        if (!meetsMinAmount && minSendChargeAmount > 0) { return showError(t('transfer.minAmountCharge', { amount: minAmountDisplay })); }
        if (!hasValidAmount) { return showError(t('transfer.validAmountRequired')); }
        if (!formData.selectedToken) { return showError(t('transfer.selectToken')); }
        return showError(t('transfer.fillAllToGenerateQR'));
      }
      handleGenerateQR();
    } else {
      if (isSubmitting) return;
      if (!canSubmit) {
        if (!isWalletConnected) { return showError(t('transfer.connectWalletToSendError')); }
        if (!hasValidRecipient) { return showError(t('transfer.validRecipientRequired')); }
        if (amountNum <= 0) { return showError(t('transfer.amountGreaterThanZero')); }
        if (!meetsMinAmount && minSendChargeAmount > 0) { return showError(t('transfer.minAmountSend', { amount: minAmountDisplay })); }
        if (!hasValidAmount) { return showError(t('transfer.validAmountRequired')); }
        if (!selectedTokenData) { return showError(t('transfer.selectTokenWithBalance')); }
        return showError(t('transfer.cannotSendNow'));
      }
      const fakeEv = { preventDefault: () => {} };
      handleSubmit(fakeEv).catch((err) => {
        console.error('[Transfer] handleSubmit rejeitou:', err);
        setIsSubmitting(false);
        setTransferStep(null);
        showError(err?.message || t('transfer.sendErrorRetry'));
      });
    }
  };

  // Enquanto verifica/conecta a carteira (evita mostrar "conecte carteira" no delay do mobile)
  if (isWalletInitializing) {
    return (
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-xl rounded-3xl p-6 border border-purple-500 border-opacity-20">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-6" title="Nexusprime">
          Nexusprime
        </h2>
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <Loader2 size={48} className="mb-4 text-purple-400 animate-spin" />
          <p className="text-center">{t('transfer.checkingWallet')}</p>
        </div>
      </div>
    );
  }

  // Sem carteira: formulário visível para ambientação; ações que dependem da carteira ficam desabilitadas (canSubmit/canGenerateQR já exigem isWalletConnected)
  return (
    <>
    <form onSubmit={transferMode === 'cobranca' ? (e) => { e.preventDefault(); handleGenerateQR(); } : handleSubmit} className="bg-gray-800 bg-opacity-50 backdrop-blur-xl rounded-3xl p-6 border border-purple-500 border-opacity-20">
      <h2 className="text-2xl font-bold text-white mb-4">{t('transfer.title')}</h2>

      {!isWalletConnected && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2">
          <AlertCircle size={20} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/90">{t('transfer.exploreWithoutWallet')}</p>
        </div>
      )}

      {/* Modo: Enviar ou Cobrança + Botão Ler QR + Ajuda */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex gap-2 p-1 bg-gray-700/50 rounded-lg w-fit">
          <button
            type="button"
            onClick={() => { setTransferMode('enviar'); setPaymentRequestURL(''); setPaymentRequestPayload(''); setShowQRInline(false); setShowQRModal(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${transferMode === 'enviar' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {t('transfer.send')}
          </button>
          <button
            type="button"
            onClick={() => { setTransferMode('cobranca'); setFormData(prev => ({ ...prev, recipient: '' })); setIsRecipientFromList(false); setPaymentRequestURL(''); setPaymentRequestPayload(''); setShowQRInline(false); setShowQRModal(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${transferMode === 'cobranca' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {t('transfer.charge')}
          </button>
        </div>
        
        {/* Botão Ler QR (apenas modo Enviar); desabilitado sem carteira; se limite de taxas atingido, abre modal Quitar taxas */}
        {transferMode === 'enviar' && (
          <button
            type="button"
            disabled={!isWalletConnected}
            onClick={() => {
              if (!isWalletConnected) return;
              if (isOverLimit) {
                onOpenPendingFeeModal?.('voluntary');
                return;
              }
              setShowQRScanner(true);
            }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition border border-gray-600 ${isWalletConnected ? 'bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white' : 'bg-gray-700/30 text-gray-500 cursor-not-allowed'}`}
            title={isWalletConnected ? t('transfer.scanQRTitle') : t('transfer.connectWalletToReadQR')}
          >
            <ScanLine size={18} />
            {t('transfer.readQR')}
          </button>
        )}

        {/* Ajuda contextual da tela (Envia/Cobra) */}
        <button
          type="button"
          onClick={() => {
            if (transferMode === 'enviar') setShowSendHelp(true);
            else setShowChargeHelp(true);
          }}
          className="ml-auto w-7 h-7 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-sm"
          aria-label={t('transfer.helpButton')}
        >
          ?
        </button>
      </div>

      {/* Lista de Stablecoins (Enviar: tokens da rede com seus saldos; Cobrança: tokens da rede) */}
      <div className="mb-6">
        <label className="block text-gray-300 mb-2">
          {transferMode === 'cobranca' ? t('transfer.tokenToReceive') : t('transfer.assetToSend')}
        </label>
        {tokensToShow.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {tokensToShow.map(token => (
              <button
                key={token.symbol}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, selectedToken: token.symbol }))}
                className={`flex items-center justify-between p-3 rounded-lg border transition ${
                  formData.selectedToken === token.symbol
                    ? 'bg-purple-900/40 border-purple-500 text-white'
                    : 'bg-gray-700/50 border-gray-600 text-gray-300 hover:border-purple-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  {TOKEN_ICONS[token.symbol] ? (
                    <img 
                      src={TOKEN_ICONS[token.symbol]} 
                      alt={token.symbol} 
                      className="w-8 h-8"
                    />
                  ) : (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      token.symbol === 'USDC'
                        ? 'bg-blue-500'
                        : 'bg-green-500'
                    }`}>
                      {token.symbol.charAt(0)}
                    </div>
                  )}
                  <span className="font-medium">{token.symbol}</span>
                </div>
                <span className={isWalletConnected && token.balance != null ? 'text-green-400 font-mono' : 'text-gray-500'}>
                  {isWalletConnected && token.balance != null ? formatCurrencyDisplay(token.balance, 'USD') : '—'}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-gray-700/50 rounded-lg p-4 text-center text-gray-400">
            <p>{transferMode === 'cobranca' ? t('transfer.selectNetworkWithTokens') : t('transfer.noStablecoinInWallet')}</p>
            <p className="text-sm mt-1">{t('header.network')}: {selectedNetwork?.name}</p>
          </div>
        )}
      </div>

      {/* Destinatário (Enviar) ou "Você receberá" (Cobrança) */}
      <div className="mb-6" ref={walletPickerRef}>
        {transferMode === 'cobranca' ? (
          <>
            <label className="block text-gray-300 mb-2">{t('transfer.recipientYouReceive')}</label>
            <div className={`relative flex items-stretch rounded-lg border overflow-hidden transition-colors duration-200 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent ${chargeAddressCopyFeedback ? 'border-green-500/60 bg-green-900/25' : 'border-gray-600'}`}>
              <div className={`flex-1 min-w-0 flex items-center justify-start font-mono text-sm p-3 h-12 min-h-12 overflow-hidden transition-colors duration-200 ${chargeAddressCopyFeedback ? 'bg-green-900/20 text-green-400' : `bg-gray-700 text-white ${chargeAddressDisplayMode === 'full' ? 'break-all' : chargeAddressDisplayMode === 'hidden' ? 'flex-wrap gap-x-0.5 gap-y-1 content-center' : 'truncate'}`}`}>
                {chargeAddressCopyFeedback ? (
                  t('transfer.addressCopied')
                ) : walletAddress ? (
                  chargeAddressDisplayMode === 'hidden' ? (
                    <span className="flex items-center flex-wrap gap-x-0.5 gap-y-1 max-h-full overflow-hidden justify-start" aria-label={t('transfer.characterCount', { count: walletAddress.length })}>
                      {Array.from({ length: walletAddress.length }, (_, i) => (
                        <span key={i} className="w-1.5 h-1.5 rounded-full bg-white shrink-0" aria-hidden />
                      ))}
                    </span>
                  ) : chargeAddressDisplayMode === 'short' ? (
                    formatAddress(walletAddress, 5, 5)
                  ) : (
                    walletAddress
                  )
                ) : (
                  <span className="text-gray-500">{t('transfer.connectWalletToSeeAddress')}</span>
                )}
              </div>
              <button
                type="button"
                disabled={!walletAddress}
                onClick={() => walletAddress && setChargeAddressDisplayMode((m) => (m === 'short' ? 'full' : 'short'))}
                className={`flex items-center justify-center w-12 shrink-0 border-l border-gray-600 transition ${!walletAddress ? 'opacity-50 cursor-not-allowed bg-gray-700/50' : chargeAddressDisplayMode === 'short' ? 'bg-purple-600/50 text-purple-200' : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'}`}
                title={t('transfer.addressShorten')}
              >
                <Shrink size={18} />
              </button>
              <button
                type="button"
                disabled={!walletAddress}
                onClick={() => walletAddress && setChargeAddressDisplayMode((m) => (m === 'hidden' ? 'full' : 'hidden'))}
                className={`flex items-center justify-center w-12 shrink-0 border-l border-gray-600 transition ${!walletAddress ? 'opacity-50 cursor-not-allowed bg-gray-700/50' : chargeAddressDisplayMode === 'hidden' ? 'bg-purple-600/50 text-purple-200' : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'}`}
                title={t('transfer.addressHide')}
              >
                <EyeOff size={18} />
              </button>
              <button
                type="button"
                disabled={!walletAddress}
                onClick={() => walletAddress && copyChargeAddress()}
                className={`flex items-center justify-center w-12 shrink-0 border-l border-gray-600 transition duration-200 ${!walletAddress ? 'opacity-50 cursor-not-allowed bg-gray-700/50' : chargeAddressCopyFeedback ? 'bg-green-600/80 text-green-100' : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'}`}
                title={t('transfer.addressCopy')}
              >
                {chargeAddressCopyFeedback ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </>
        ) : (
          <>
        <label className="block text-gray-300 mb-2">{t('transfer.recipient')}</label>
        <div className={`relative flex items-stretch rounded-lg border overflow-hidden transition-colors ${
          hasValidRecipient
            ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-400/40 focus-within:border-green-400'
            : formData.recipient && !hasValidRecipient
              ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-300/40 focus-within:border-red-300'
              : 'border-gray-600 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent'
        }`}>
        <input
          type="text"
          name="recipient"
            placeholder="0x..."
          value={formData.recipient}
            onChange={isRecipientFromList ? undefined : handleChange}
            readOnly={isRecipientFromList}
            className={`flex-1 bg-gray-700 text-white p-3 placeholder-gray-500 focus:outline-none min-w-0 ${
              isRecipientFromList ? 'cursor-default' : ''
            }`}
          />
          {/* Botão limpar - aparece quando endereço foi selecionado da lista */}
          {isRecipientFromList && formData.recipient && (
            <button
              type="button"
              onClick={clearRecipient}
              className="flex items-center justify-center w-10 bg-gray-700 text-gray-400 hover:text-red-400 hover:bg-gray-600 transition shrink-0 border-l border-gray-600"
              title={t('transfer.clearAddress')}
            >
              <X size={18} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowWalletPicker((v) => !v)}
            className="flex items-center justify-center w-12 bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600 transition shrink-0 border-l border-gray-600"
            title={t('transfer.selectWalletFromList')}
          >
            <Bookmark size={20} />
          </button>
          {onOpenCarteiras && (
            <>
              <button
                type="button"
                onClick={() => setShowWalletQRScanner(true)}
                className="flex items-center justify-center w-12 bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600 transition shrink-0 border-l border-gray-600"
                title={t('transfer.readWalletQRTitle')}
              >
                <ScanLine size={18} />
              </button>
              <button
                type="button"
                onClick={() => {
                  const addr = formData.recipient?.trim();
                  const isValid = addr && /^0x[a-fA-F0-9]{40}$/.test(addr);
                  const inList = isValid && wallets.some((w) => w.address?.toLowerCase() === addr.toLowerCase());
                  onOpenCarteiras(inList ? undefined : (isValid ? addr : undefined));
                }}
                className="flex items-center justify-center gap-1.5 w-auto px-3 bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600 transition shrink-0 border-l border-gray-600"
                title={
                  (() => {
                    const addr = formData.recipient?.trim();
                    const isValid = addr && /^0x[a-fA-F0-9]{40}$/.test(addr);
                    const inList = isValid && wallets.some((w) => w.address?.toLowerCase() === addr.toLowerCase());
                    return isValid && !inList ? t('transfer.addWalletToRegister') : t('transfer.openWalletList');
                  })()
                }
              >
                <UserPlus size={18} />
              </button>
            </>
          )}
        </div>
        {showWalletPicker && (
          <div className="absolute z-50 mt-1 w-full min-w-[280px] max-h-64 overflow-auto rounded-lg bg-gray-800 border border-purple-500/30 shadow-xl">
            {wallets.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                {t('transfer.noWalletsRegistered')}
              </div>
            ) : (
              <ul className="py-2">
                {wallets.map((wallet) => (
                  <li key={wallet.id}>
                    <button
                      type="button"
                      onClick={() => selectRecipientFromWallet(wallet.address)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-700/80 transition flex flex-col gap-0.5"
                    >
                      <span className="font-medium text-white">{wallet.name}</span>
                      {wallet.email && (
                        <span className="text-xs text-gray-400">{wallet.email}</span>
                      )}
                      <span className="text-xs text-gray-500 font-mono">
                        {formatAddress(wallet.address, 10, 8)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {formData.recipient && !hasValidRecipient && (
          <p className="text-red-400 text-sm mt-1">{t('transfer.invalidAddress')}</p>
        )}
          </>
        )}
      </div>

      {/* Moeda e Valor — quando lockDisplayCurrency (Configurações), seletor de moeda fica oculto aqui */}
      <div className={`grid gap-4 mb-6 ${lockDisplayCurrency ? 'grid-cols-1' : 'grid-cols-2'}`}>
        {!lockDisplayCurrency && (
          <div className="relative" ref={currencyDropdownRef}>
            <label className="block text-gray-300 mb-2">{t('transfer.currencyLabel')}</label>
            <button
              type="button"
              onClick={() => setCurrencyDropdownOpen((v) => !v)}
              className="w-full flex items-center gap-2 bg-gray-700 text-white rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-left"
              aria-label={t('transfer.currencyLabel')}
              aria-expanded={currencyDropdownOpen}
              aria-haspopup="listbox"
            >
              <img
                src={formData.currency === 'USD' ? usFlag : brazilFlag}
                alt=""
                className="w-6 h-4 object-cover rounded shrink-0"
                aria-hidden
              />
              <span>{formData.currency === 'USD' ? 'USD ($)' : 'BRL (R$)'}</span>
            </button>
            {currencyDropdownOpen && (
              <ul
                role="listbox"
                className="absolute z-10 mt-1 w-full bg-gray-700 rounded-lg shadow-lg border border-gray-600 overflow-hidden"
              >
                <li role="option" aria-selected={formData.currency === 'USD'}>
                  <button
                    type="button"
                    onClick={() => handleCurrencySelect('USD')}
                    className="w-full flex items-center gap-2 p-3 text-white hover:bg-gray-600 text-left"
                  >
                    <img src={usFlag} alt="" className="w-6 h-4 object-cover rounded shrink-0" aria-hidden />
                    <span>USD ($)</span>
                  </button>
                </li>
                <li role="option" aria-selected={formData.currency === 'BRL'}>
                  <button
                    type="button"
                    onClick={() => handleCurrencySelect('BRL')}
                    className="w-full flex items-center gap-2 p-3 text-white hover:bg-gray-600 text-left"
                  >
                    <img src={brazilFlag} alt="" className="w-6 h-4 object-cover rounded shrink-0" aria-hidden />
                    <span>BRL (R$)</span>
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
        <div>
          <label className="block text-gray-300 mb-2">{t('transfer.amount')}</label>
          <div className={`relative rounded-lg border transition-colors ${
            hasValidAmount
              ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-400/40 focus-within:border-green-400'
              : formData.amount.trim() !== '' && !hasValidAmount
                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-300/40 focus-within:border-red-300'
                : 'border-gray-600 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent'
          }`}>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {formData.currency === 'USD' ? '$' : 'R$'}
            </span>
          <input
              type="text"
              inputMode="decimal"
            name="amount"
              placeholder={formData.currency === 'USD' ? '0.00' : '0,00'}
            value={formData.amount}
              onChange={handleAmountChange}
              onBlur={handleAmountBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && transferMode === 'enviar') {
                  e.preventDefault();
                  handleSubmitButtonClick(e);
                }
              }}
              className="w-full bg-gray-700 text-white rounded-lg p-3 pl-8 placeholder-gray-500 focus:outline-none border-0 focus:ring-0"
            />
          </div>
          {minSendChargeAmount > 0 && (
            <p className={`text-sm mt-1.5 ${amountNum > 0 && !meetsMinAmount ? 'text-amber-400 font-medium' : 'text-gray-500'}`}>
              {amountNum > 0 && !meetsMinAmount
                ? t('transfer.increaseAmountToMin', { amount: minAmountDisplay })
                : t('transfer.minAmountHint', { amount: minAmountDisplay })}
            </p>
          )}
        </div>
      </div>

      {/* Timer de Atualização da Cotação */}
      <div className="mb-4 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-gray-400">
          <Clock size={14} />
          <span>{t('transfer.quote', { rate: formatCurrencyDisplay(rates.BRL ?? 0, 'BRL') })}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-24 h-1.5 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000"
              style={{ width: `${timerProgress}%` }}
            />
          </div>
          <span className={`text-xs ${ratesLoading ? 'text-purple-400' : 'text-gray-500'}`}>
            {ratesLoading ? (
              <RefreshCw size={12} className="animate-spin" />
            ) : (
              `${secondsUntilUpdate}s`
            )}
          </span>
        </div>
      </div>

      {/* Resumo da Transação */}
      <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 mb-6">
        <div className="flex justify-between text-gray-300 mb-2">
          <span>{t('transfer.summaryAmount')}</span>
          <div className="text-right">
            <span>{formatCurrencyDisplay(amountNum, formData.currency)}</span>
            <span className="text-gray-500 text-sm ml-2">
              ({formatCurrencyDisplay(convertedAmount, otherCurrency)})
            </span>
          </div>
        </div>

        {/* Taxas — modo Enviar: só taxa de transferência */}
        {transferMode === 'enviar' && feeEnabled && transferFee > 0 && (
          <div className="flex justify-between text-gray-300 mb-2">
            <span className="flex items-center gap-1">
              <Percent size={12} />
              {t('transfer.fee', { percent: feePercent })}
            </span>
            <div className="text-right">
              <span>{formatCurrencyDisplay(transferFee, formData.currency)}</span>
              <span className="text-gray-500 text-sm ml-2">
                ({formatCurrencyDisplay(convert(transferFee, formData.currency, otherCurrency), otherCurrency)})
              </span>
            </div>
          </div>
        )}

        {/* Taxas — modo Receber: transferência + recebimento, com somatório */}
        {transferMode === 'cobranca' && feeEnabled && totalFee > 0 && (
          <>
            {transferFee > 0 && (
              <div className="flex justify-between text-gray-400 text-sm mb-1">
                <span className="flex items-center gap-1"><Percent size={10} /> {t('transfer.transferFee', { percent: feePercent })}</span>
                <span>{formatCurrencyDisplay(transferFee, formData.currency)}</span>
              </div>
            )}
            {billingFee > 0 && (
              <div className="flex justify-between text-gray-400 text-sm mb-1">
                <span className="flex items-center gap-1"><Percent size={10} /> {t('transfer.billingFee', { percent: feeBillingPercent })}</span>
                <span>{formatCurrencyDisplay(billingFee, formData.currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-300 mb-2">
              <span className="font-medium">{t('transfer.totalFees', { percent: feePercent + feeBillingPercent })}</span>
              <span>{formatCurrencyDisplay(totalFee, formData.currency)}</span>
            </div>

            {/* Quem cobra sempre paga a taxa de recebimento; pode absorver a taxa de transferência (Configurações) */}
            <div className="bg-gray-800/60 rounded-lg p-3 mb-2">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>{t('transfer.youPayFees')}</span>
                <span className="font-medium text-gray-200">{formatCurrencyDisplay(billerPaysFee, formData.currency)}</span>
              </div>
              <p className="text-[10px] text-gray-500">
                {absorbTransferFee ? t('transfer.youAbsorbTransferFeeYes') : t('transfer.youAbsorbTransferFeeNo')}
              </p>
            </div>

            {/* Parte embutida no valor cobrado ao pagador (só taxa de transferência, se não absorver) */}
            {embeddedFee > 0 && (
              <div className="flex justify-between text-gray-300 text-sm mb-1">
                <span>{t('transfer.feeChargedToPayer')}</span>
                <span>{formatCurrencyDisplay(embeddedFee, formData.currency)}</span>
              </div>
            )}
          </>
        )}

        <div className="border-t border-gray-600 my-2" />
        <div className="flex justify-between text-white font-bold">
          <span>{transferMode === 'cobranca' ? t('transfer.payerPays') : t('transfer.total')}</span>
          <div className="text-right">
            <span className="text-lg">
              {formatCurrencyDisplay(totalInSelectedCurrency, formData.currency)}
            </span>
            <span className="text-gray-400 text-sm ml-2 font-normal">
              ({formatCurrencyDisplay(convertedTotal, otherCurrency)})
            </span>
          </div>
        </div>
        
        {/* Valor que será debitado em stablecoin (apenas modo Enviar) */}
        {transferMode === 'enviar' && selectedTokenData && amountNum > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-600">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">{t('transfer.debitIn', { token: formData.selectedToken })}</span>
              <span className={`font-mono ${hasSufficientBalance ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrencyDisplay(requiredAmountUSD, 'USD')}
              </span>
            </div>
            {transferFee > 0 && (
              <p className="text-xs text-gray-500 mt-1 text-right">
                {t('transfer.feePendingHint', { amount: formatCurrencyDisplay(transferFee, formData.currency) })}
              </p>
            )}
            {!hasSufficientBalance && selectedTokenData.balance != null && (
              <p className="text-red-400 text-xs mt-1 text-right">
                {t('transfer.insufficientBalance', { amount: formatCurrencyDisplay(selectedTokenData.balance, 'USD') })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Status das Transações (confirmadas na blockchain) */}
      {(txHash || feesTxHash) && (
        <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-lg space-y-2">
          {txHash && (
            <div>
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle size={16} />
                <span>
                  {transferConfirmed
                    ? lastConfirmedDetail
                      ? `${new Date(lastConfirmedDetail.at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })} - ${formatCurrencyDisplay(lastConfirmedDetail.amount ?? 0, 'USD')} - ${getAddressOrWalletName(wallets, lastConfirmedDetail.recipient || '', 6, 4).display || formatAddress(lastConfirmedDetail.recipient || '', 6, 4)}`
                      : t('transfer.txConfirmed')
                    : t('transfer.txPending')}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {getBlockExplorerTxUrl(selectedNetwork?.chainId, txHash) ? (
                  <a
                    href={getBlockExplorerTxUrl(selectedNetwork?.chainId, txHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline"
                  >
                    {t('transfer.viewOnBlockExplorer')}
                  </a>
                ) : (
                  <span className="break-all">{t('transfer.hash')} {txHash}</span>
                )}
              </p>
              {SHOW_ADD_TO_WALLET_FROM_MOVEMENTS && lastConfirmedDetail?.recipient && onOpenCarteiras && !wallets.some((w) => w?.address && String(w.address).toLowerCase() === String(lastConfirmedDetail.recipient).toLowerCase()) && !hasShownAddToListPrompt(lastConfirmedDetail.recipient) && (
                <p className="text-xs mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      markAddToListPromptShown(lastConfirmedDetail.recipient);
                      const recipient = lastConfirmedDetail.recipient;
                      const addrLower = String(recipient).toLowerCase();
                      let walletData = null;
                      if (recipientWalletPayload && String(recipientWalletPayload.address).toLowerCase() === addrLower) {
                        walletData = recipientWalletPayload;
                      } else if (receiverProfileFromPaymentRequest && String(receiverProfileFromPaymentRequest.address || '').toLowerCase() === addrLower) {
                        walletData = receiverProfileFromPaymentRequest;
                      }
                      onOpenCarteiras(recipient, walletData);
                    }}
                    className="text-purple-400 hover:text-purple-300 underline"
                  >
                    {t('common.addToWalletList')}
                  </button>
                </p>
              )}
            </div>
          )}
          {feesTxHash && (
            <div className="pt-2 border-t border-green-500/30">
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle size={16} />
                <span>{feeConfirmed ? t('transfer.feeConfirmed') : t('transfer.feePending')}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {getBlockExplorerTxUrl(selectedNetwork?.chainId, feesTxHash) ? (
                  <a
                    href={getBlockExplorerTxUrl(selectedNetwork?.chainId, feesTxHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline"
                  >
                    {t('transfer.viewOnBlockExplorer')}
                  </a>
                ) : (
                  <span className="break-all">{t('transfer.hash')} {feesTxHash}</span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Botão de ação principal — log de eventos para diagnóstico mobile; em "enviar" dispara submit via botão oculto */}
      <button
        type="button"
        disabled={(transferMode === 'cobranca' && generatingPaymentLink) || (transferMode === 'enviar' && cooldownSecondsLeft > 0)}
        onClick={handleSubmitButtonClick}
        style={{
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
          cursor: transferMode === 'cobranca' && generatingPaymentLink ? 'wait' : 'pointer',
          minHeight: 56,
        }}
        className={`w-full font-semibold py-4 px-4 rounded-2xl flex items-center justify-center gap-2 transition select-none ${
          (transferMode === 'enviar' ? canSubmit && cooldownSecondsLeft === 0 : canGenerateQR) && !(transferMode === 'cobranca' && generatingPaymentLink)
            ? transferMode === 'cobranca'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white active:opacity-80'
              : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white active:opacity-80'
            : 'bg-gray-600 text-gray-400'
        }`}
      >
        {transferMode === 'cobranca' ? (
          generatingPaymentLink ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              {t('transfer.generatingLink')}
            </>
          ) : (
            <>
              <QrCode size={20} />
              {t('transfer.generateQR')}
            </>
          )
        ) : cooldownSecondsLeft > 0 ? (
          <>
            <Clock size={20} />
            {t('transfer.waitBeforeRetry', { seconds: cooldownSecondsLeft })}
          </>
        ) : isSubmitting ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            {transferStep === 'recipient' && t('transfer.sendingToRecipient')}
            {transferStep === 'fee' && t('transfer.sendingFee')}
            {!transferStep && (txHash ? t('transfer.txPending') : t('transfer.processing'))}
          </>
        ) : (
          <>
        <Send size={20} />
            {t('transfer.transferToken', { token: formData.selectedToken || 'Stablecoin' })}
          </>
        )}
      </button>

      {isSubmitting && transferMode === 'enviar' && (
        <p className="text-gray-400 text-xs text-center mt-2">
          {txHash ? t('transfer.awaitingConfirmation') : t('transfer.metaMaskConfirmDelay')}
        </p>
      )}

      {/* Avisos */}
      {!hasAnyStablecoinBalance && hasTokens && isWalletConnected && (
        <p className="text-yellow-400 text-sm text-center mt-4">
          {t('transfer.needStablecoins', { network: selectedNetwork?.name })}
        </p>
      )}

      {/* Versão alternativa: QR e link exibidos inline na tela */}
      {transferMode === 'cobranca' && (paymentRequestPayload || paymentRequestURL) && showQRInline && (
        <div className="mt-6 p-4 rounded-xl border border-purple-500/30 bg-gray-800/80">
          <p className="text-gray-300 text-sm font-medium mb-3">{t('transfer.chargeDisplayTitle')}</p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-shrink-0 p-3 bg-white rounded-lg">
              <QRCodeSVG value={paymentRequestPayload || paymentRequestURL} size={180} level="M" />
            </div>
            <div className="flex-1 w-full min-w-0">
              <p className="text-gray-400 text-xs mb-2">{t('transfer.payerMustBeLoggedIn')}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={paymentRequestURL}
                  className="flex-1 min-w-0 bg-gray-700 text-gray-300 text-xs p-2 rounded-lg truncate"
                />
                <button
                  type="button"
                  onClick={copyPaymentLink}
                  className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm shrink-0"
                >
                  <Copy size={16} />
                  {t('transfer.copy')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Painel de diagnóstico em tempo de execução (apenas administradores) */}
      {isAdminUser && (
        <div className="mt-8 p-4 rounded-xl border border-dashed border-purple-500/50 bg-gray-900/70 text-xs text-gray-200 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-purple-300">
              {t('transfer.adminDiagnosticsTitle', 'Diagnóstico do fluxo de envio')}
            </p>
            <p className="text-[10px] text-gray-500">
              {t('transfer.adminDiagnosticsOnly', 'Visível apenas para administradores')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                {t('transfer.adminDiagnosticsNetwork', 'Rede e carteira')}
              </p>
              <p>
                <span className="text-gray-400">Wallet chainId:</span>{' '}
                <code className="font-mono text-[11px]">{walletChain || '—'}</code>
              </p>
              <p>
                <span className="text-gray-400">App chainId:</span>{' '}
                <code className="font-mono text-[11px]">{selectedNetwork?.chainId || '—'}</code>
              </p>
              <p>
                <span className="text-gray-400">Mismatch agora:</span>{' '}
                <span className={isNetworkMismatch(normalizeChainId(walletChain), normalizeChainId(selectedNetwork?.chainId)) ? 'text-red-400' : 'text-green-400'}>
                  {isNetworkMismatch(normalizeChainId(walletChain), normalizeChainId(selectedNetwork?.chainId)) ? 'SIM' : 'NÃO'}
                </span>
              </p>
            </div>

            <div className="space-y-1.5">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                {t('transfer.adminDiagnosticsForm', 'Formulário atual')}
              </p>
              <p>
                <span className="text-gray-400">Modo:</span>{' '}
                <span className="font-mono">{transferMode}</span>
              </p>
              <p className="truncate">
                <span className="text-gray-400">Destinatário:</span>{' '}
                <code className="font-mono text-[11px] break-all">{formData.recipient || '—'}</code>
              </p>
              <p>
                <span className="text-gray-400">Valor informado:</span>{' '}
                <span className="font-mono">
                  {formData.amount || '—'} {formData.currency}
                </span>
              </p>
              <p>
                <span className="text-gray-400">Token selecionado:</span>{' '}
                <span className="font-mono">{formData.selectedToken || '—'}</span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                {t('transfer.adminDiagnosticsFlags', 'Flags de validação')}
              </p>
              <p>
                hasValidRecipient:{' '}
                {transferMode === 'cobranca' ? (
                  <span className="text-gray-500">{t('transfer.adminDiagnosticsNotApplicable', 'não se aplica')}</span>
                ) : (
                  <span className={hasValidRecipient ? 'text-green-400' : 'text-red-400'}>{String(!!hasValidRecipient)}</span>
                )}
              </p>
              <p>hasValidAmount: <span className={hasValidAmount ? 'text-green-400' : 'text-red-400'}>{String(!!hasValidAmount)}</span></p>
              <p>hasTokens: <span className={hasTokens ? 'text-green-400' : 'text-red-400'}>{String(!!hasTokens)}</span></p>
              <p>
                hasSufficientBalance:{' '}
                {transferMode === 'cobranca' ? (
                  <span className="text-gray-500">{t('transfer.adminDiagnosticsNotApplicable', 'não se aplica')}</span>
                ) : (
                  <span className={hasSufficientBalance ? 'text-green-400' : 'text-red-400'}>{String(!!hasSufficientBalance)}</span>
                )}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                {t('transfer.adminDiagnosticsBalances', 'Saldos')}
              </p>
              <p>
                <span className="text-gray-400">Saldo token:</span>{' '}
                <span className="font-mono">
                  {selectedTokenData?.balance != null ? formatCurrencyDisplay(selectedTokenData.balance, 'USD') : '—'} USD
                </span>
              </p>
              <p>
                <span className="text-gray-400">Requerido p/ enviar:</span>{' '}
                {transferMode === 'cobranca' ? (
                  <span className="text-gray-500">{t('transfer.adminDiagnosticsNotApplicable', 'não se aplica')}</span>
                ) : (
                  <span className="font-mono">{formatCurrencyDisplay(requiredAmountUSD || 0, 'USD')} USD</span>
                )}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                {t('transfer.adminDiagnosticsRules', 'Regras finais')}
              </p>
              <p>
                <span className="text-gray-400">Erro de regra:</span>{' '}
                {transferMode === 'cobranca' ? (
                  <span className="text-gray-500">{t('transfer.adminDiagnosticsNotApplicable', 'não se aplica')} (modo Receber)</span>
                ) : (
                  <code className="font-mono text-[11px]">{formValidationErrorPreview || 'null'}</code>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </form>

    {/* Ajuda – Modo Enviar */}
    <Modal
      isOpen={showSendHelp}
      onClose={() => setShowSendHelp(false)}
      title={t('transfer.helpSendTitle')}
    >
      <div className="space-y-3 text-sm text-gray-200">
        <p>{t('transfer.helpSendIntro')}</p>
        <ol className="list-decimal list-inside space-y-1 text-gray-300">
          <li>{t('transfer.helpSendStep1')}</li>
          <li>{t('transfer.helpSendStep2')}</li>
          <li>{t('transfer.helpSendStep3')}</li>
          <li>{t('transfer.helpSendStep4')}</li>
          <li>{t('transfer.helpSendStep5')}</li>
          <li>{t('transfer.helpSendStep6')}</li>
        </ol>
        <p className="text-xs text-gray-400">
          {t('transfer.helpSendTip')}
        </p>
      </div>
    </Modal>

    {/* Ajuda – Modo Receber */}
    <Modal
      isOpen={showChargeHelp}
      onClose={() => setShowChargeHelp(false)}
      title={t('transfer.helpChargeTitle')}
    >
      <div className="space-y-3 text-sm text-gray-200">
        <p>{t('transfer.helpChargeIntro')}</p>
        <ol className="list-decimal list-inside space-y-1 text-gray-300">
          <li>{t('transfer.helpChargeStep1')}</li>
          <li>{t('transfer.helpChargeStep2')}</li>
          <li>{t('transfer.helpChargeStep3')}</li>
          <li>{t('transfer.helpChargeStep4')}</li>
          <li>{t('transfer.helpChargeStep5')}</li>
        </ol>
        <p className="text-xs text-gray-400">
          {t('transfer.helpChargeTip')}
        </p>
      </div>
    </Modal>

    {/* Modal: QR Code para receber (versão em modal) */}
    <Modal
      isOpen={showQRModal && !!(paymentRequestPayload || paymentRequestURL)}
      onClose={() => setShowQRModal(false)}
      title={t('transfer.qrChargeTitle')}
    >
      <div className="flex flex-col items-center gap-4">
        <p className="text-gray-400 text-sm text-center">
          {t('transfer.payerMustBeLoggedIn')}
        </p>

        <div className="p-4 bg-white rounded-xl">
          <QRCodeSVG value={paymentRequestPayload || paymentRequestURL} size={200} level="M" />
        </div>

        <div className="w-full flex gap-2">
          <button
            type="button"
            onClick={copyPaymentLink}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm"
          >
            <Copy size={16} />
            {t('transfer.copyLink')}
          </button>
          <button
            type="button"
            onClick={() => { setShowQRModal(false); setShowQRInline(true); }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-purple-500 text-purple-400 hover:bg-purple-500/10 rounded-xl text-sm"
            title={t('transfer.onScreenTitle')}
          >
            <Maximize2 size={16} />
            {t('transfer.onScreen')}
          </button>
        </div>
      </div>
    </Modal>

    {/* Scanner de QR Code (modo Enviar: QR para receber / endereço) */}
    <QRScanner
      isOpen={showQRScanner}
      onClose={() => setShowQRScanner(false)}
      onScan={handleQRScanned}
    />

    {/* Scanner de QR da carteira (Configurações) — abre cadastro com dados lidos */}
    <QRScanner
      isOpen={showWalletQRScanner}
      onClose={() => setShowWalletQRScanner(false)}
      onScan={handleWalletQRScanned}
    />
  </>
  );
}